// Shapes of a staged file import — the Assistant's file import and the
// `import` action button — and the transport each host supplies.
//
// The server side is ONE module (`twasta_assistant_engine.data_import`) behind
// three hosts: the platform (`/api/assistant/import/*` +
// `/api/record-import/*`), the downloaded runtime and J2EE
// (`/app/<project>/api/record-import/*`, J2EE via its Python sidecar). These
// types are the wire contract they share.

export interface ImportIssue {
  severity: 'error' | 'warning' | 'info'
  message: string
  record?: number
  line?: number
  form_no?: string
  row?: number
  field?: string
}

export interface ImportCounts {
  records: number
  new: number
  update: number
  /** Matched records identical to what is stored — seeded as 'retrieved'. */
  unchanged?: number
  not_found?: number
  conflicts?: number
  no_key?: number
  errors: number
  warnings: number
  truncated?: number
  file_rows?: number
}

/** How a lookup cell's written value became a code (or why it did not).
 *  `method`: display = exact name, xref = the other party's own code,
 *  alias = a remembered alias, vector / text = closest by description. */
export interface ImportMatch {
  /** What the file said. */
  from: string
  code?: string
  display?: string
  method?: 'display' | 'xref' | 'alias' | 'vector' | 'text'
  score?: number | null
  /** Confirming it would teach the cross-reference / the master's aliases. */
  learnable?: boolean
  candidates: Array<{ code: string; display?: string; score?: number }>
}

/** One prepared record — the grid's EmbedRecord seed plus the source lines. */
export interface PreparedImportRecord {
  id: string
  status: 'unsaved' | 'dirty' | 'retrieved'
  primaryKey?: string
  header: Record<string, unknown>
  originalHeader?: Record<string, unknown>
  details: Record<string, Array<Record<string, unknown>>>
  fieldErrors: Record<string, string>
  recordError: string | null
  sourceRows: number[]
  changedFields?: string[]
  /** Header lookup cells resolved from a written value, by field key. Child
   *  rows carry theirs as `_resolved` / `_candidates`. */
  resolved?: Record<string, ImportMatch>
  /** Header lookup cells that need a pick, with the closest candidates. */
  candidates?: Record<string, ImportMatch>
}

/** One confirmed "their value → our code" pair sent to `learn`. */
export interface ImportLearnItem {
  /** Child form; omitted for the header. */
  form_no?: string
  field: string
  value: string
  code: string
  /** The record's values — bind a cross-reference's scope (the customer). */
  header: Record<string, unknown>
  row?: Record<string, unknown>
}

export interface ImportLearnResult {
  learned: number
  skipped: number
  stores: string[]
  notes: string[]
  format?: { learned?: boolean; created?: boolean; sender?: string; file_kind?: string; note?: string }
}

export type ImportMode = 'add' | 'edit' | 'upsert'

export interface PreparedImport {
  obj_name: string
  /** What the screen is called (its menu title) — shown instead of obj_name. */
  obj_title?: string
  mode: ImportMode
  mapping: Record<string, string>
  primary_key: string[]
  counts: ImportCounts
  issues: ImportIssue[]
  records: PreparedImportRecord[]
  summary?: string
  filename?: string
  /** On-screen label per field key (a line field as `<form>.<key>`). */
  labels?: Record<string, string>
}

export interface ImportFileGrid {
  columns: { index: number; raw: string; clean: string; section: string | null }[]
  rows: { line: number; values: string[] }[]
  total_rows: number
  truncated: boolean
  sheet?: string | null
  sheets: string[]
  /** 1-based column indices whose values are image URLs (for thumbnail display). */
  image_cols?: number[]
  /** The rows were READ from a document (PDF / picture / Word) by the AI. */
  document?: boolean
  /** The uploaded document itself — what the preview's "File" view shows
   *  (fetched through the transport's `file`). Absent for a data file. */
  original?: ImportOriginal
}

/** Where one line read from a document is written: its page (1-based) and
 *  the boxes of its values, `[x, y, w, h]` as fractions of the page. */
export interface ImportLineLocation { page: number; boxes: number[][] }

/** The uploaded document. A PDF the server can draw also carries its pages
 *  (size in points) and where each line read from it sits; the preview then
 *  draws the pages itself and marks the selected record on them. */
export interface ImportOriginal {
  name: string
  kind: 'pdf' | 'image' | 'docx' | string
  mime: string
  pages?: Array<{ w: number; h: number }>
  locations?: Record<string, ImportLineLocation>
}

/** Who sent a file, as the import worked it out. */
export interface ImportSender {
  code: string
  display?: string
  /** picked (at upload) · recognised (by the profile's identifiers) ·
   *  document (the document's own customer) · layout (a spreadsheet layout). */
  how?: string
  field?: string
  profile?: { status?: string; source?: string; has_instructions?: boolean } | null
}

/** What reading a DOCUMENT found (PDF / picture / Word). */
export interface ImportReading {
  sender?: ImportSender | null
  /** digital · scanned_print · handwritten · mixed · photo · printout */
  document_kind?: string
  /** text = read from the text layer; file = read from the page itself. */
  input_mode?: string
  notes?: string
}

/** The preview body: the file's rows (left pane) + the prepared records. */
export interface ImportPreviewData {
  import_id: string
  filename: string
  file: ImportFileGrid
  prepared: PreparedImport | null
  reading?: ImportReading
}

/** One sender's import profile for one file type — and, optionally, one
 *  document type (see import_profiles.py). */
export interface ImportSenderProfile {
  obj_name: string
  sender_code: string
  sender_display?: string
  file_kind: 'pdf' | 'image' | 'docx' | 'spreadsheet' | string
  /** The document type this prompt is for; `*` = any type. */
  doc_type?: string
  doc_kind?: string
  identifiers: string[]
  /** What the reading follows — the person's text once edited. */
  instructions: string
  /** The AI's latest analysis, kept apart from a person's instructions. */
  suggested?: string
  /** ai · user */
  source?: string
  /** draft (an AI draft nobody reviewed) · active */
  status?: string
  samples?: number
  updated_by?: string
  updated_at?: string | null
  /** A PDF password is stored (encrypted). The password itself is never sent. */
  has_password?: boolean
}

/** An upload whose PDF needs a password: ask, then `unlock`. */
export interface RecordImportLocked {
  import_id: string
  filename: string
  document: true
  needs_password: true
  wrong_password?: boolean
  message: string
}

export function isLockedUpload(x: RecordImportInspect | RecordImportLocked | RecordImportReview): x is RecordImportLocked {
  return !!(x as RecordImportLocked).needs_password
}

// ── The REVIEW step: sender, type and prompt, before a document is read ──

/** One keyword that matched (see import_patterns.py). */
export interface ImportKeywordHit {
  kind: string
  value: string
  weight?: number
  /** title = one of the document's headings · body */
  where?: string
  /** What was found, as found. */
  text?: string
}

/** A rule's score in a detection (candidates when two tie). */
export interface ImportRuleScore {
  scope: 'sender' | 'doc_type' | string
  sender_code: string
  doc_type: string
  label?: string
  status?: string
  score: number
  kinds?: string[]
  matched?: ImportKeywordHit[]
}

/** Who sent a document — a record of the screen's own sender lookup
 *  (`lookup`), a record of another master found by its name in the vector
 *  index (`master`: `object` + `code`), or a sender known by NAME with no
 *  master record (`name`). `key` is what their keywords and prompts are
 *  stored under. `{kind: "none"}` = no specific sender. */
export interface ImportSenderCandidate {
  kind: 'lookup' | 'master' | 'name' | string
  key: string
  code: string
  object: string
  display: string
  score?: number
  /** How alike the names are, 0–1 (1 = the same name). */
  name_match?: number
  /** lookup · known (this screen knows them) · master · name */
  source?: string
}

export interface ImportSenderSearch {
  query: string
  best: ImportSenderCandidate | null
  candidates: ImportSenderCandidate[]
  /** The sender known by this name — no master record. */
  name_only: ImportSenderCandidate | null
}

export type ImportSenderPick = ImportSenderCandidate | { kind: 'none' }

export interface ImportDetection {
  /** `code` = the sender's KEY; `ref_code` = their code in `object`'s master.
   *  how: picked · password · keywords · master · lookup · known · name · ai */
  sender: { code: string; display?: string; how?: string; kind?: string; object?: string;
            ref_code?: string; key?: string; kinds?: string[];
            matched?: ImportKeywordHit[]; score?: number } | null
  /** The person said the document has no specific sender. */
  no_sender?: boolean
  /** how: picked · keywords · ai */
  doc_type: { code: string; label?: string; how?: string; matched?: ImportKeywordHit[];
              score?: number | null } | null
  /** exact (active rules) · likely (a rule is still an AI draft) · partial · ambiguous · none */
  confidence: string
  ambiguous?: { sender?: boolean; doc_type?: boolean }
  candidates?: { senders?: ImportRuleScore[]; doc_types?: ImportRuleScore[] }
}

/** What an upload answers for a DOCUMENT in the review flow: nothing is read
 *  yet — the person checks who sent it, what it is and the prompt, then
 *  Processes it (`read`). */
export interface RecordImportReview {
  stage: 'review'
  import_id: string
  filename: string
  document: true
  file_kind: string
  /** The text the keywords were matched in — a scan's is its transcript. */
  text: { source: 'text_layer' | 'docx' | 'transcript' | '' | string; chars: number;
          preview: string; truncated: boolean }
  detection: ImportDetection
  /** Who it could be, when no one record is clearly the sender. */
  sender_candidates?: ImportSenderCandidate[]
  /** Keywords alone did not settle it: ask again with the AI ("Detecting the
   *  format and sender…"). */
  needs_ai?: boolean
  /** The document types the screen knows, for the picker. */
  doc_types: Array<{ code: string; label: string }>
  prompt: {
    text: string
    /** type = this sender's prompt for this type · sender = their any-type
     *  prompt · pending = drafted, kept once the sender is known · "" = none */
    scope: string
    source: string
    status: string
    doc_type: string
    /** The AI's newer suggestion for a prompt a person owns. */
    suggested?: string
    updated_by?: string
  }
  /** The screen designer's instructions — always applied, read-only here. */
  screen_instructions: string
  document_kind?: string
  sender_field: string
  ai: { used: string[]; checks?: unknown[]; tried?: boolean }
  notes: string[]
  /** May change the prompt and the keywords (the `import_profiles` right). */
  can_edit: boolean
  /** Why the AI could not help (not set up, out of credit). */
  ai_unavailable?: string
  password?: { used?: string; remembered?: boolean; note?: string }
}

export function isReview(x: unknown): x is RecordImportReview {
  return !!x && (x as RecordImportReview).stage === 'review'
}

/** The Process call: what the person saw or changed in the review step. */
export interface RecordImportReadBody {
  sender?: string
  docType?: string
  /** The edited prompt — omitted = the one shown (only managers may send one). */
  prompt?: string
  /** Keep the edited prompt for this sender + type. */
  savePrompt?: boolean
  /** Read with the screen's general instructions only — no sender, no type. */
  generic?: boolean
}

// ── The keyword registry (import_rules.py) ─────────────────────────────

export interface ImportKeywordPattern {
  /** email · phone · tax_id · name · city · postal · phrase · pattern */
  kind: string
  value: string
  weight?: number
  /** any · title (the document's headings only) */
  zone?: string
  required?: boolean
  exclude?: boolean
  label?: string
}

export interface ImportKeywordRule {
  scope: 'sender' | 'doc_type'
  sender_code: string
  doc_type: string
  label?: string
  patterns: ImportKeywordPattern[]
  /** The AI's proposals for a rule a person owns. */
  suggested?: ImportKeywordPattern[]
  settings?: Record<string, unknown>
  /** ai · user */
  source?: string
  /** draft (an AI proposal) · active */
  status?: string
  hits?: number
  updated_by?: string
  updated_at?: string | null
  last_hit_at?: string | null
}

export interface ImportRulesList {
  rules: ImportKeywordRule[]
  doc_types: Array<{ code: string; label: string; global?: boolean; senders?: string[]; status?: string }>
  /** Values printed on documents of several senders — they identify nobody. */
  stop_list?: Array<{ kind: string; value: string; senders: string[] }>
}

export interface ImportRuleEdit {
  scope: 'sender' | 'doc_type'
  /** A sender rule: the sender (code or name). A type rule: `*` = any sender. */
  sender: string
  doc_type?: string
  label?: string
  patterns: ImportKeywordPattern[]
}

export interface ImportRuleSaveResult {
  rule: ImportKeywordRule
  /** Keywords that could not be used, and why. */
  dropped: Array<{ pattern: unknown; reason: string }>
  /** The uniqueness check against the stored samples — advice. */
  check?: { ok: boolean; problems: string[]; dropped: Array<{ pattern: unknown; reason: string }>;
            conflicts: Array<{ doc_type: string; sender_code: string; label?: string }> }
}

export interface ImportProfilesList {
  can_manage: boolean
  sender_field: string
  learn: boolean
  profiles: ImportSenderProfile[]
  /** This person's "Recognise the sender and document type" for the screen
   *  (their saved choice, else the screen's default). */
  detect?: boolean
}

export interface ImportProfileEdit {
  /** The sender's code or name (resolved by the server). */
  sender: string
  file_kind: string
  instructions: string
  identifiers: string[]
  doc_kind?: string
  /** The document type the prompt is for; omitted / `*` = any type. */
  doc_type?: string
}

/** What a closing preview sends to remember. */
export interface ImportLearnBody {
  items: ImportLearnItem[]
  /** The SAVED records (header + lines) — the sender's format is learned
   *  from them (an AI draft for a sender with no profile yet). */
  records: Array<{ header: Record<string, unknown>; details: Record<string, unknown[]> }>
}

/** What happened in the preview, reported back when it closes. */
export interface ImportOutcome {
  total: number
  saved: number
  failed: number
  pending: number
  /** Records identical to what is stored — shown as retrieved, nothing to save. */
  unchanged?: number
  /** Records the user left out of the import (dropped from the grid unsaved). */
  leftOut?: number
}

// ── The `import` action button's map step ─────────────────────────────

export interface ImportColumnProfile {
  index: number
  raw: string
  clean: string
  section: string | null
  filled: number
  distinct: number
  samples: string[]
}

export interface ImportTargetField {
  key: string
  label: string
  type: string
  /** `TABLE(DISPLAY, DATA)` — a lookup field, which the import resolves
   *  from a name / description / the other party's code. */
  lookup?: string
  /** What the field accepts in the file (said for lookup fields). */
  accepts?: string
  primary_key?: boolean
  required?: boolean
  engine_filled?: boolean
  hidden?: boolean
  read_only?: boolean
}

export interface ImportTargetForm {
  form_no: string
  title: string
  role: string
  primary_key: string[]
  fields: ImportTargetField[]
}

export interface ImportKeyCandidate {
  column: number
  header: string
  samples: string[]
  row_number: boolean
  sampled: number
  found: number
  agreement: number | null
}

/** What the map step needs about a staged file. */
export interface RecordImportInspect {
  import_id: string
  filename: string
  sheet?: string | null
  sheets: string[]
  rows: number
  warnings: string[]
  columns: ImportColumnProfile[]
  forms: ImportTargetForm[]
  /** `{"#N": "<key>" | "<form_no>.<key>"}` — the name-match first guess. */
  suggestion: Record<string, string>
  key_candidates: ImportKeyCandidate[]
  mode: ImportMode
  lock_mode: boolean
  title: string
  /** The file was a document the AI read into these columns. */
  document?: boolean
  /** From the reading: `{"<key>" | "<form_no>.<key>": ["#N", ...]}` description
   *  columns that help find each lookup field's record. */
  suggested_hints?: Record<string, string[]>
  /** From the reading: the header field that makes its lines one record. */
  suggested_group_by?: string | null
  /** Who sent it (a document's reading, or a remembered spreadsheet layout). */
  sender?: ImportSender | null
  document_kind?: string
  /** How a locked PDF was opened (typed / stored) and whether it was remembered. */
  password?: { used?: string; remembered?: boolean; note?: string }
}

/** Which button is importing into which object. */
export interface RecordImportTarget {
  project: string
  objName: string
  action: string
}

export interface RecordImportPrepareOptions {
  map: Record<string, string>
  mode?: ImportMode
  groupBy?: string | null
  monthFirst?: boolean
  sheet?: string | null
  /** `{"<key>" | "<form_no>.<key>": ["#N", ...]}` — other file columns that
   *  help find a lookup field's record (and are searched when it is blank). */
  resolveHints?: Record<string, string[]>
}

export interface RecordImportSummary {
  kind: 'import_preview'
  import_id: string
  obj_name: string
  mode: ImportMode
  filename: string
  counts: ImportCounts
  top_issues: ImportIssue[]
}

/** A refused call, in words a person can act on (plus a map's problems). */
export interface ImportErrorInfo {
  message: string
  problems: string[]
}

/** The `/record-import/*` calls, bound to one host's server. */
export interface RecordImportTransport {
  /** ``sender`` (optional) — who sent a document: their profile is used to read it.
   *  ``review`` — a DOCUMENT is not read: the answer is its review step. */
  upload: (t: RecordImportTarget, file: File,
    opts?: { sender?: string; review?: boolean; useAi?: boolean; generic?: boolean }) =>
    Promise<RecordImportInspect | RecordImportLocked | RecordImportReview>
  /** Open a password-protected PDF with the typed password and read it (answers
   *  like an upload — or, with `review`, with its review step). A wrong one
   *  rejects with a `password_required` detail. */
  unlock?: (t: RecordImportTarget, importId: string,
    body: { password: string; remember: boolean; sender?: string; review?: boolean;
            useAi?: boolean; generic?: boolean }) =>
    Promise<RecordImportInspect | RecordImportReview>
  /** The review step again with the sender / type the person picked. */
  review?: (t: RecordImportTarget, importId: string,
    body: { sender?: string; senderRef?: ImportSenderPick; docType?: string; useAi?: boolean }) =>
    Promise<RecordImportReview>
  /** Who sent it, by name: the screen's lookup, known senders, every master. */
  searchSenders?: (t: RecordImportTarget, q: string) => Promise<ImportSenderSearch>
  /** Remember this person's "Recognise the sender and document type". */
  setPreferences?: (t: RecordImportTarget, body: { detect: boolean }) => Promise<unknown>
  /** Process: read the reviewed document (answers like an upload). Without it
   *  the window never asks for the review step. */
  read?: (t: RecordImportTarget, importId: string, body: RecordImportReadBody) => Promise<RecordImportInspect>
  inspect: (t: RecordImportTarget, importId: string, sheet: string) => Promise<RecordImportInspect>
  prepare: (t: RecordImportTarget, importId: string, opts: RecordImportPrepareOptions) => Promise<RecordImportSummary>
  preview: (t: RecordImportTarget, importId: string) => Promise<ImportPreviewData>
  /** The uploaded DOCUMENT itself, for the preview's "File" view — or, with
   *  `page`, that page of a PDF drawn as a PNG. Optional — without it the
   *  preview shows only what was read from the document. */
  file?: (t: RecordImportTarget, importId: string, page?: number) => Promise<Blob>
  /** Remember the matches kept or picked in a saved import. Optional — a host
   *  without it simply does not offer "Remember these matches". */
  learn?: (t: RecordImportTarget, importId: string, body: ImportLearnBody) => Promise<ImportLearnResult>
  /** Sender profiles of the target screen. Optional — without them the window
   *  offers no profile manager. */
  listProfiles?: (t: RecordImportTarget) => Promise<ImportProfilesList>
  saveProfile?: (t: RecordImportTarget, edit: ImportProfileEdit) => Promise<ImportSenderProfile>
  deleteProfile?: (t: RecordImportTarget, senderCode: string, fileKind: string, docType?: string) => Promise<unknown>
  /** The keyword registry (managers only). Optional — without it the profile
   *  manager offers no Keywords tab. */
  listRules?: (t: RecordImportTarget) => Promise<ImportRulesList>
  saveRule?: (t: RecordImportTarget, edit: ImportRuleEdit) => Promise<ImportRuleSaveResult>
  setRuleStatus?: (t: RecordImportTarget,
    body: { scope: string; sender_code: string; doc_type: string; status: 'active' | 'draft' }) => Promise<unknown>
  deleteRule?: (t: RecordImportTarget,
    body: { scope: string; sender_code: string; doc_type: string }) => Promise<unknown>
  /** Store (encrypted) or — with "" — remove a sender's PDF password. */
  setProfilePassword?: (t: RecordImportTarget,
    body: { sender: string; file_kind: string; password: string }) => Promise<{ has_password: boolean }>
  /** Read a refused call's reason. Defaults to `importErrorInfo`. */
  describeError?: (e: unknown, fallback: string) => ImportErrorInfo
}

/** The server's reason for a refused call. Understands an axios-style error
 *  (`response.data.detail`), the runtime client's ApiError (`detail` / `body`)
 *  and a plain Error; `detail` may be a string, a `{message, problems}` map or
 *  the `{error: {message}}` envelope the rights gate answers with. */
export function importErrorInfo(e: unknown, fallback: string): ImportErrorInfo {
  const any = e as {
    response?: { data?: { detail?: unknown } }
    detail?: unknown
    body?: { detail?: unknown }
    message?: string
  } | null
  const detail = any?.response?.data?.detail ?? any?.body?.detail ?? any?.detail
  if (typeof detail === 'string' && detail) return { message: detail, problems: [] }
  if (detail && typeof detail === 'object') {
    const d = detail as { message?: string; problems?: string[]; error?: { message?: string } }
    if (d.error?.message) return { message: d.error.message, problems: [] }
    return { message: d.message || fallback, problems: Array.isArray(d.problems) ? d.problems : [] }
  }
  return { message: (e instanceof Error && e.message) || any?.message || fallback, problems: [] }
}
